import React, { useEffect, useState } from "react";
import { SettingRow } from "../SettingRow";
import {
  ConsentManagementVariant,
  CustomConsentManagementVariant,
} from "../../../../../../common/service/server/dto/CMPSettingsDto";
import { SelectMenuDropdown, Table, Button as AquiloButton } from "@truevault/aquilo";
import { TableDensity, TableStyle } from "@truevault/aquilo/src/components/table/Table";
import { OverrideableRegion } from "../../../../superorg/settings/ConsentBannerSettings";
import { TrashIcon } from "@heroicons/react/24/outline";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { SelectMenuDropdownItem } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { Iso3166Region } from "../../../../../../common/service/server/Types";
import { ConfirmDialog } from "../../../../dialog";

/**
 * Kind of a long story. This component is used in a React Final Form, and the predefined consent region table part uses
 * radio buttons from MUI. We want the same visual appearance for the radio buttons of the custom region variants. Since
 * the data model for the custom regions is pretty complicated, we can't map it directly to a `field` that the MUI radio
 * groups would understand. Additionally, the custom regions are grouped by consent variant (each is a row). So, we have
 * to manually manage the mapping from the way we store the custom region data on the backend like this:
 *
 * [{"region":"US-AL","variant":"NONE"},{"region":"US-AK","variant":"NONE"},{"region":"US-AZ","variant":"INTERACTIVE_DEFAULT_NO_CONSENT"},...]
 *
 * to a way we can represent it as data for the grouped-by-variant-per-row table like this:
 *
 * [{"id":"variant-row-0","regions":["US-AL","US-AK"],"variant":"NONE"},{"id":"variant-row-1","regions":["US-AZ"],"variant":"INTERACTIVE_DEFAULT_NO_CONSENT"},...]
 *
 * This happens at the boundary of this component: the incoming `customVariants` prop and the `onUpdate` callback expect
 * custom variant data in the first format, so we convert to the 2nd format for display & interaction in the table, then
 * convert back when updating.
 *
 * Because we aren't storing information about the table layout this doesn't support multiple rows with the same consent
 * variant - they get magically merged into one row. So we have a dialog that warns the user they'll be visually merged,
 * if they try to switch a row to have the same variant as an existing row.
 */
export interface ConsentManagementVariantClientRow {
  id: string; // Unique identifier for the row
  regions: string[]; // List of selected regions
  variant: ConsentManagementVariant; // Selected consent management variant
}

// Reusable function to map ConsentManagementVariantClientRow to CustomConsentManagementVariant
const clientRowsToCustomVariants = (
  rows: ConsentManagementVariantClientRow[],
): CustomConsentManagementVariant[] => {
  return rows.flatMap(({ regions, variant }) =>
    regions.map((region) => ({
      region,
      variant,
    })),
  );
};

// Reusable function to transform CustomConsentManagementVariant to ConsentManagementVariantClientRow
const customVariantsToClientRows = (
  variants: CustomConsentManagementVariant[],
  addFakeRow: boolean = false,
): ConsentManagementVariantClientRow[] => {
  const groupedByVariant = variants.reduce<Record<string, string[]>>((acc, { region, variant }) => {
    if (!acc[variant]) {
      acc[variant] = [];
    }
    acc[variant].push(region);
    return acc;
  }, {});

  const rows = Object.entries(groupedByVariant).map(([variant, regions], index) => ({
    id: `variant-row-${index}`,
    regions,
    variant: variant as ConsentManagementVariant,
  }));

  if (addFakeRow) {
    rows.push({
      id: `variant-row-${rows.length}`,
      regions: [],
      variant:
        Object.values(ConsentManagementVariant).find(
          (v) => !rows.some((row) => row.variant === v),
        ) || ConsentManagementVariant.NONE,
    });
  }

  return rows;
};

interface ConsentManagementVariantCustomRowProps {
  tableHeader: string;
  customVariants?: CustomConsentManagementVariant[];
  allowedRegions: OverrideableRegion[];
  onUpdate: (updatedRows: CustomConsentManagementVariant[]) => void;
}

export const ConsentManagementVariantCustomRow = ({
  tableHeader,
  customVariants,
  allowedRegions,
  onUpdate,
}: ConsentManagementVariantCustomRowProps) => {
  const [localRows, setLocalRows] = useState<ConsentManagementVariantClientRow[]>();
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    rowId: string;
    variant: ConsentManagementVariant;
  } | null>(null);

  useEffect(() => {
    // Update `localRows` when `customVariants` prop changes
    if (customVariants && customVariants.length > 0) {
      setLocalRows(customVariantsToClientRows(customVariants, isAddingRow));
    } else {
      setLocalRows([
        {
          id: "variant-row-0",
          regions: [],
          variant: ConsentManagementVariant.NONE,
        },
      ]);
    }
  }, [customVariants, isAddingRow]);

  const regionOptions: SelectMenuDropdownItem<Iso3166Region>[] = allowedRegions.map((region) => ({
    label: region.label,
    value: region.region,
    previewLabel: region.abbreviation,
  }));

  const handleRegionChange = (rowId: string, region: string) => {
    setIsAddingRow(false);
    const updatedRows = localRows.map((row) =>
      row.id === rowId
        ? {
            ...row,
            /* the `variant` field is set by React Final Form based on the `variant-row-*` ephemeral field for the row's corresponding RadioGroup */
            regions: row.regions.includes(region)
              ? row.regions.filter((r) => r !== region) // Remove region if already selected
              : [...row.regions, region], // Add region if not selected
          }
        : row,
    );
    setLocalRows(updatedRows);
    onUpdate(clientRowsToCustomVariants(updatedRows)); // Use reusable function
  };

  const handleVariantChange = (rowId: string, variant: ConsentManagementVariant) => {
    const existingRow = localRows.find((row) => row.variant === variant && row.id !== rowId);

    if (existingRow) {
      setPendingChange({ rowId, variant });
      setConfirmDialogOpen(true);
    } else {
      applyVariantChange(rowId, variant);
    }
  };

  const applyVariantChange = (rowId: string, variant: ConsentManagementVariant) => {
    setIsAddingRow(false);
    const updatedRows = localRows.map((row) => (row.id === rowId ? { ...row, variant } : row));
    setLocalRows(updatedRows);
    onUpdate(clientRowsToCustomVariants(updatedRows));
  };

  const addRow = () => {
    setIsAddingRow(true);
    setLocalRows(customVariantsToClientRows(customVariants, true));
  };

  const removeRow = (rowId: string) => {
    setIsAddingRow(false);
    const updatedRows = localRows.filter((row) => row.id !== rowId);

    // Reset to one blank row if all rows are removed
    if (updatedRows.length === 0) {
      updatedRows.push({
        id: "variant-row-0",
        regions: [],
        variant: ConsentManagementVariant.NONE,
      });
    }

    setLocalRows(updatedRows);
    onUpdate(clientRowsToCustomVariants(updatedRows)); // Use reusable function
  };

  return (
    <>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          if (pendingChange) {
            // The transform functions handle "merging" and removing the old row, all we have to do here is call apply.
            applyVariantChange(pendingChange.rowId, pendingChange.variant);
          }
          setConfirmDialogOpen(false);
          setPendingChange(null);
        }}
        confirmTitle="Combine override rows?"
        confirmText="Combine"
        cancelText="Don't Combine"
        contentText={`You are changing the banner override for the following states: ${localRows
          ?.find((row) => row.id === pendingChange?.rowId)
          ?.regions.map((r) => r.replace("US-", ""))
          .join(
            ", ",
          )}. There is already a row configured for the selected banner type for the following states: ${localRows
          ?.find((row) => row.variant === pendingChange?.variant && row.id !== pendingChange?.rowId)
          ?.regions.map((r) => r.replace("US-", ""))
          .join(
            ", ",
          )}. Because these states will now share a banner type, the rows will be combined. Continue?`}
      />
      <SettingRow maxWidthChildren={true} vertical={true}>
        <div className="tw-boundary max-w-[896px] overflow-x-auto" style={{ marginTop: "-0.5rem" }}>
          <Table<ConsentManagementVariantClientRow>
            tableStyle={TableStyle.OUTLINED}
            zeroState={
              <p className="text-sm text-gray-500">
                No overrides defined. Get started by adding one.
              </p>
            }
            tableDensity={TableDensity.COMPACT}
            keyExtractor={(item) => item.id}
            columns={[
              {
                key: "region",
                header: <div>{tableHeader}</div>,
                className: "align-middle",
                render: (item) => (
                  <div className="flex items-center">
                    <SelectMenuDropdown
                      allowMultipleSelected={true}
                      isVerticallyStacked={false}
                      showAllSelected={true}
                      noSelectedErrorMessage="Choose states"
                      maxWidth="320px"
                      dropdownWidthPx={360}
                      menuMaxHeightPx={280}
                      items={regionOptions.map((option) => ({
                        ...option,
                        isSelected: item.regions.includes(option.value),
                        disabledTooltip: "This state is already selected in another row",
                        isDisabled: localRows.some(
                          (row) => row.id !== item.id && row.regions.includes(option.value),
                        ),
                      }))}
                      onSelect={async (value) => handleRegionChange(item.id, value)}
                    />
                  </div>
                ),
              },
              {
                key: "optin",
                header: <div className="flex justify-center w-full">Opt-In</div>,
                className: "text-center align-middle",
                render: (item) => (
                  <div className="flex justify-center">
                    <RadioGroup
                      className="align-items-center d-flex"
                      onChange={(_e) => {
                        handleVariantChange(
                          item.id,
                          ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
                        );
                      }}
                      value={
                        localRows.find((row) => row.id === item.id)?.variant ===
                        ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT
                          ? "selected"
                          : null
                      }
                    >
                      <FormControlLabel
                        value="selected"
                        label={null}
                        style={{ marginRight: "0" }}
                        control={<Radio color="primary" />}
                        disabled={!(item.regions?.length > 0)}
                      />
                    </RadioGroup>
                  </div>
                ),
              },
              {
                key: "optout",
                header: <div className="flex justify-center w-full">Opt-Out</div>,
                className: "text-center align-middle",
                render: (item) => (
                  <div className="flex justify-center">
                    <RadioGroup
                      className="align-items-center d-flex"
                      onChange={(_e) => {
                        handleVariantChange(
                          item.id,
                          ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
                        );
                      }}
                      value={
                        localRows.find((row) => row.id === item.id)?.variant ===
                        ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT
                          ? "selected"
                          : null
                      }
                    >
                      <FormControlLabel
                        value="selected"
                        label={null}
                        style={{ marginRight: "0" }}
                        control={<Radio color="primary" />}
                        disabled={!(item.regions?.length > 0)}
                      />
                    </RadioGroup>
                  </div>
                ),
              },
              {
                key: "informational",
                header: <div className="flex justify-center w-full">Informational</div>,
                className: "text-center align-middle",
                render: (item) => (
                  <div className="flex justify-center">
                    <RadioGroup
                      className="align-items-center d-flex"
                      onChange={(_e) => {
                        handleVariantChange(item.id, ConsentManagementVariant.INFORMATIONAL_ONLY);
                      }}
                      value={
                        localRows.find((row) => row.id === item.id)?.variant ===
                        ConsentManagementVariant.INFORMATIONAL_ONLY
                          ? "selected"
                          : null
                      }
                    >
                      <FormControlLabel
                        value="selected"
                        label={null}
                        style={{ marginRight: "0" }}
                        control={<Radio color="primary" />}
                        disabled={!(item.regions?.length > 0)}
                      />
                    </RadioGroup>
                  </div>
                ),
              },
              {
                key: "none",
                header: <div className="flex justify-center w-full">None</div>,
                className: "text-center align-middle",
                render: (item) => (
                  <div className="flex justify-center">
                    <RadioGroup
                      className="align-items-center d-flex"
                      onChange={(_e) => {
                        handleVariantChange(item.id, ConsentManagementVariant.NONE);
                      }}
                      value={
                        localRows.find((row) => row.id === item.id)?.variant ===
                        ConsentManagementVariant.NONE
                          ? "selected"
                          : null
                      }
                    >
                      <FormControlLabel
                        value="selected"
                        label={null}
                        style={{ marginRight: "0" }}
                        control={<Radio color="primary" />}
                        disabled={!(item.regions?.length > 0)}
                      />
                    </RadioGroup>
                  </div>
                ),
              },
              {
                key: "_actions",
                header: <div />,
                className: "text-center align-middle",
                render: (item) => (
                  <div className="flex justify-center items-center">
                    <AquiloButton
                      color="tertiary"
                      variant="round"
                      isCircular
                      onClick={() => removeRow(item.id)}
                      aria-label="Remove this row"
                      disabled={localRows?.length === 1 && !(item.regions?.length > 0)}
                    >
                      <TrashIcon className="w-[20px] h-[20px] text-gray-500" />
                    </AquiloButton>
                  </div>
                ),
              },
            ]}
            onAddItem={isAddingRow || localRows?.length > 3 ? null : addRow}
            addButtonLabel="Add another row"
            data={localRows}
          />
        </div>
      </SettingRow>
    </>
  );
};
